// =============================================================================
// BP-10-04: Case Status Badge Component
// BrightPath Platform - Status Indicator for Legal Cases
// =============================================================================

import React from 'react';
import {
  FileSearch,
  FileText,
  Search,
  Scale,
  UserCheck,
  Gavel,
  BadgeDollarSign,
  CheckCircle,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import type { CaseStatusType } from '../types/attorney';
import { CASE_STATUS_LABELS } from '../types/attorney';

// =============================================================================
// Types
// =============================================================================

interface CaseStatusBadgeProps {
  status: CaseStatusType;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

// =============================================================================
// Status Configuration
// =============================================================================

const statusConfig: Record<
  CaseStatusType,
  {
    icon: React.ElementType;
    bgColor: string;
    textColor: string;
    borderColor: string;
  }
> = {
  intake: {
    icon: FileSearch,
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200',
  },
  investigation: {
    icon: Search,
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
  },
  filed: {
    icon: FileText,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
  },
  discovery: {
    icon: Search,
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    borderColor: 'border-purple-200',
  },
  mediation: {
    icon: UserCheck,
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    borderColor: 'border-orange-200',
  },
  trial: {
    icon: Gavel,
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
  },
  settlement: {
    icon: BadgeDollarSign,
    bgColor: 'bg-sage-50',
    textColor: 'text-sage-700',
    borderColor: 'border-sage-200',
  },
  closed: {
    icon: CheckCircle,
    bgColor: 'bg-navy-50',
    textColor: 'text-navy-700',
    borderColor: 'border-navy-200',
  },
};

// =============================================================================
// Size Configuration
// =============================================================================

const sizeConfig = {
  sm: {
    badge: 'px-2 py-0.5 text-xs',
    icon: 'w-3 h-3',
    gap: 'gap-1',
  },
  md: {
    badge: 'px-2.5 py-1 text-sm',
    icon: 'w-4 h-4',
    gap: 'gap-1.5',
  },
  lg: {
    badge: 'px-3 py-1.5 text-sm',
    icon: 'w-5 h-5',
    gap: 'gap-2',
  },
};

// =============================================================================
// Component
// =============================================================================

export function CaseStatusBadge({
  status,
  size = 'md',
  showIcon = true,
  className,
}: CaseStatusBadgeProps) {
  const config = statusConfig[status];
  const sizes = sizeConfig[size];
  const Icon = config.icon;
  
  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full border',
        config.bgColor,
        config.textColor,
        config.borderColor,
        sizes.badge,
        sizes.gap,
        className
      )}
    >
      {showIcon && <Icon className={sizes.icon} />}
      <span>{CASE_STATUS_LABELS[status]}</span>
    </span>
  );
}

// =============================================================================
// Status Progress Indicator
// =============================================================================

const statusOrder: CaseStatusType[] = [
  'intake',
  'investigation',
  'filed',
  'discovery',
  'mediation',
  'trial',
  'settlement',
  'closed',
];

interface CaseStatusProgressProps {
  currentStatus: CaseStatusType;
  className?: string;
}

export function CaseStatusProgress({ currentStatus, className }: CaseStatusProgressProps) {
  const currentIndex = statusOrder.indexOf(currentStatus);
  
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {statusOrder.map((status, index) => {
        const config = statusConfig[status];
        const Icon = config.icon;
        const isActive = index === currentIndex;
        const isPast = index < currentIndex;
        const isClosed = currentStatus === 'closed';
        
        return (
          <React.Fragment key={status}>
            <div
              className={cn(
                'flex items-center justify-center w-8 h-8 rounded-full transition-all',
                isActive && cn(config.bgColor, config.textColor),
                isPast && 'bg-sage/20 text-sage',
                !isActive && !isPast && 'bg-gray-100 text-gray-400'
              )}
              title={CASE_STATUS_LABELS[status]}
            >
              <Icon className="w-4 h-4" />
            </div>
            {index < statusOrder.length - 1 && (
              <div
                className={cn(
                  'h-0.5 w-4 transition-all',
                  isPast || (isClosed && index < statusOrder.length - 1)
                    ? 'bg-sage'
                    : 'bg-gray-200'
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// =============================================================================
// Status Select Options (for forms)
// =============================================================================

export function getStatusOptions() {
  return statusOrder.map((status) => ({
    value: status,
    label: CASE_STATUS_LABELS[status],
    icon: statusConfig[status].icon,
  }));
}

export default CaseStatusBadge;


